import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  Ban,
  Paperclip,
  X,
  Eye,
  Download,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
} from "lucide-react";
import axiosInstance from "../../axiosInstance";
import GeneralTable from "../../components/Table/GeneralTable";
import StatusCard from "../../components/StatusCard/StatusCard";
import PageHeader from "../../components/ui/PageHeader";
import SummaryCard from "../../components/ui/SummaryCard";
import { normalizeStatus } from "../../components/ui/StatusBadge";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import { ErrorState } from "../../components/ui/Feedback";
import "./Reimbursements.scss";

const API = `${process.env.REACT_APP_API_EMPLOYEE_URL}/reimbursement`;

// Attachment limits (same as the FileInput this page used before)
const MAX_FILE_SIZE = 5000000; // 5MB
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "image/jpg",
];
const ERROR_SIZE = "File size exceeds the limit of 5MB";
const ERROR_TYPE =
  "Invalid file type. Only JPEG, PNG, and PDF files are allowed.";

const inr = (value) => `₹${(value || 0).toLocaleString("en-IN")}`;

const formatFileSize = (bytes) =>
  bytes >= 1000000
    ? `${(bytes / 1000000).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1000))} KB`;

// Base64 string -> Blob
const base64ToBlob = (file, fileType) => {
  const byteCharacters = atob(file);
  const bytes = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    bytes[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([bytes], { type: fileType });
};

const FileTypeIcon = ({ fileType }) => {
  const type = fileType || "";
  if (type.includes("image")) return <ImageIcon size={16} aria-hidden="true" />;
  if (type.includes("excel") || type.includes("spreadsheet")) {
    return <FileSpreadsheet size={16} aria-hidden="true" />;
  }
  return <FileText size={16} aria-hidden="true" />;
};

function Reimbursements() {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [reimbursementTypes, setReimbursementTypes] = useState([]);
  const [typesError, setTypesError] = useState(false);
  const [selectedReimbursementId, setSelectedReimbursementId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pendingCancelId, setPendingCancelId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const fileInputRef = useRef(null);

  // ---- data -------------------------------------------------------------------
  const fetchReimbursementTypes = useCallback(async () => {
    setTypesError(false);
    try {
      const response = await axiosInstance.get(`${API}/list`);
      if (response.status === 200) {
        setReimbursementTypes(
          Array.isArray(response.data) ? response.data : [],
        );
      } else {
        console.error("Failed to fetch reimbursement types.");
        setTypesError(true);
      }
    } catch (error) {
      console.error("Error fetching reimbursement types:", error);
      setTypesError(true);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoadError(false);
    try {
      const response = await axiosInstance.get(`${API}/employee`);
      if (response.status === 200) {
        setRequests(response.data.data || []);
      } else {
        console.error("Failed to fetch previous requests.");
        setLoadError(true);
      }
    } catch (error) {
      console.error("Error fetching previous requests:", error);
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    fetchReimbursementTypes();
  }, [fetchReimbursementTypes]);

  useEffect(() => {
    setLoading(true);
    fetchRequests().finally(() => setLoading(false));
  }, [fetchRequests]);

  const retryRequests = () => {
    setLoading(true);
    fetchRequests().finally(() => setLoading(false));
  };

  // ---- cancel ---------------------------------------------------------------------
  const handleCancelConfirmed = async () => {
    if (!pendingCancelId) return;
    setCancelling(true);
    try {
      const response = await axiosInstance.put(
        `${API}/cancel/${pendingCancelId}`,
      );
      if (response.status === 200) {
        toast.success("Request cancelled successfully.");
        fetchRequests();
      } else {
        toast.error("Failed to cancel the request.");
      }
    } catch (error) {
      console.error("Error cancelling request:", error);
      // This used to be console.log only, so a failed cancel looked like nothing happened.
      toast.error(
        `Error: ${
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred"
        }`,
      );
    } finally {
      setCancelling(false);
      setPendingCancelId(null);
    }
  };

  // ---- attachments (existing rows) ----------------------------------------------------
  const handleViewFile = (file, fileName, fileType) => {
    try {
      const fileURL = URL.createObjectURL(base64ToBlob(file, fileType));
      window.open(fileURL, "_blank");
      setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
    } catch (error) {
      console.error("Error viewing file:", error);
      toast.error("Failed to open file");
    }
  };

  const handleDownloadFile = (file, fileName, fileType) => {
    try {
      const url = URL.createObjectURL(base64ToBlob(file, fileType));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  // ---- attachment picker (new request) -----------------------------------------------------
  const clearFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setAttachment(null);
      setFileError(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setAttachment(null);
      setFileError(ERROR_SIZE);
      clearFileInput();
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setAttachment(null);
      setFileError(ERROR_TYPE);
      clearFileInput();
      return;
    }
    setFileError(null);
    setAttachment(file);
  };

  const removeAttachment = () => {
    setAttachment(null);
    setFileError(null);
    clearFileInput();
  };

  // ---- submit -----------------------------------------------------------------------------------
  const handleSubmit = async (event) => {
    event.preventDefault();

    const numericAmount = parseFloat(amount);

    if (
      !description ||
      !amount ||
      numericAmount <= 0 ||
      !remarks ||
      !selectedReimbursementId
    ) {
      toast.error(
        "Please fill out all required fields and enter a valid amount.",
      );
      return;
    }
    if (submitting) return;

    try {
      setSubmitting(true);

      const reimbursementDetails = JSON.stringify({
        description,
        amount: numericAmount,
        remarks,
        reimbursementId: selectedReimbursementId,
      });

      const formData = new FormData();
      formData.append(
        "reimbursementDetails",
        new Blob([reimbursementDetails], { type: "application/json" }),
      );
      formData.append("attachment", attachment);

      const response = await axiosInstance.post(`${API}/create`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        toast.success("Reimbursement request submitted successfully.");
        setDescription("");
        setAmount("");
        setRemarks("");
        setAttachment(null);
        setFileError(null);
        clearFileInput();
        setSelectedReimbursementId("");
        fetchRequests();
      } else {
        toast.error("Failed to submit");
      }
    } catch (error) {
      console.error("Error occurred:", error);
      toast.error(
        `${
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred"
        }`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- statistics -----------------------------------------------------------------------------------
  const stats = requests.reduce(
    (acc, req) => {
      const { key } = normalizeStatus(req.managerApprovalStatus);
      if (key === "pending") acc.pending += 1;
      else if (key === "approved") acc.approved += 1;
      else if (key === "rejected" || key === "cancelled") acc.rejected += 1;
      acc.totalAmount += req.amount || 0;
      if (req.paymentStatus === "PAID") acc.paidAmount += req.amount || 0;
      return acc;
    },
    { pending: 0, approved: 0, rejected: 0, totalAmount: 0, paidAmount: 0 },
  );

  // ---- history table ------------------------------------------------------------------------------------
  const rows = requests.map((request) => {
    const requestedOn = request.requestedOn
      ? new Date(request.requestedOn)
      : null;
    const validDate = requestedOn && !isNaN(requestedOn.getTime());

    return {
      ...request,
      formattedAmount: inr(request.amount),
      formattedDate: validDate
        ? requestedOn.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "N/A",
      dateValue: validDate ? requestedOn.getTime() : null,
      // cancelling is not possible once approved, cancelled or paid
      canCancel: !(
        request.managerApprovalStatus === "Approved" ||
        request.hrApprovalStatus === "Approved" ||
        request.managerApprovalStatus === "Canceled" ||
        request.hrApprovalStatus === "Canceled" ||
        request.paymentStatus === "PAID"
      ),
    };
  });

  const columns = [
    { header: "Description", accessor: "description" },
    {
      header: "Amount",
      accessor: "formattedAmount",
      sortAccessor: (r) => r.amount || 0,
    },
    {
      header: "Status",
      accessor: "managerApprovalStatus",
      render: (r) => <StatusCard status={r.managerApprovalStatus} />,
    },
    {
      header: "Payment",
      accessor: "paymentStatus",
      render: (r) => <StatusCard status={r.paymentStatus} />,
    },
    {
      header: "Date",
      accessor: "formattedDate",
      sortAccessor: (r) => r.dateValue,
    },
    {
      header: "Attachment",
      accessor: "fileName",
      sortable: false,
      render: (r) =>
        r.file && r.fileName ? (
          <div className="rb-attachment">
            <span className="rb-attachment__icon">
              <FileTypeIcon fileType={r.fileType} />
            </span>
            <span className="rb-attachment__name" title={r.fileName}>
              {r.fileName}
            </span>
            <button
              type="button"
              className="hr-iconbtn"
              aria-label={`View ${r.fileName}`}
              title="View file"
              onClick={() => handleViewFile(r.file, r.fileName, r.fileType)}
            >
              <Eye size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="hr-iconbtn"
              aria-label={`Download ${r.fileName}`}
              title="Download file"
              onClick={() => handleDownloadFile(r.file, r.fileName, r.fileType)}
            >
              <Download size={16} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <span className="hr-muted">No attachment</span>
        ),
    },
    {
      header: "Actions",
      key: "action",
      sortable: false,
      align: "right",
      render: (r) =>
        r.canCancel ? (
          <button
            type="button"
            className="hr-btn hr-btn--secondary rb-cancel-btn"
            aria-label={`Cancel reimbursement request: ${r.description}`}
            onClick={() => setPendingCancelId(r.reimbursementId)}
          >
            <Ban size={14} aria-hidden="true" />
            Cancel
          </button>
        ) : (
          <span className="hr-muted">No Action</span>
        ),
    },
  ];

  return (
    <div className="reimbursements-page">
      <PageHeader
        breadcrumbs={[
          { label: "Self Service", to: "/ess/my-leave" },
          { label: "Reimbursements" },
        ]}
        title="Reimbursement Management"
        subtitle="Submit and track your expense reimbursements."
      />

      <div className="hr-summary-grid">
        <SummaryCard
          tone="warning"
          icon="pending_actions"
          label="Awaiting Review"
          value={loadError ? "–" : stats.pending}
          hint="Requests in review"
          loading={loading}
        />
        <SummaryCard
          tone="success"
          icon="task_alt"
          label="Approved"
          value={loadError ? "–" : stats.approved}
          hint="Approved requests"
          loading={loading}
        />
        <SummaryCard
          tone="danger"
          icon="cancel"
          label="Rejected / Cancelled"
          value={loadError ? "–" : stats.rejected}
          hint="Not accepted"
          loading={loading}
        />
        <SummaryCard
          tone="primary"
          icon="account_balance_wallet"
          label="Total Amount"
          value={loadError ? "–" : inr(stats.totalAmount)}
          hint={`${inr(stats.paidAmount)} paid`}
          loading={loading}
        />
      </div>

      <section className="hr-card rb-card" aria-labelledby="rb-form-title">
        <h2 id="rb-form-title">New reimbursement request</h2>
        <p className="rb-card__desc">
          Describe the expense, add the amount and attach a receipt if you have
          one.
        </p>

        <form className="rb-form" onSubmit={handleSubmit}>
          <div className="rb-grid">
            <div className="rb-field">
              <label htmlFor="reimbursementType">Reimbursement type *</label>
              <select
                id="reimbursementType"
                className="hr-select__control"
                value={selectedReimbursementId}
                onChange={(e) => setSelectedReimbursementId(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select reimbursement type
                </option>
                {reimbursementTypes.map((type) => (
                  <option
                    key={type.reimbursementId}
                    value={type.reimbursementId}
                  >
                    {type.type}
                  </option>
                ))}
              </select>
              {typesError && (
                <p className="rb-error" role="alert">
                  Couldn't load the reimbursement types.{" "}
                  <button
                    type="button"
                    className="rb-link"
                    onClick={fetchReimbursementTypes}
                  >
                    Try again
                  </button>
                </p>
              )}
            </div>

            <div className="rb-field">
              <label htmlFor="amount">Amount *</label>
              <div className="rb-currency">
                <span className="rb-currency__symbol" aria-hidden="true">
                  ₹
                </span>
                <input
                  type="number"
                  id="amount"
                  className="hr-input"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="rb-field">
              <label htmlFor="description">Description *</label>
              <input
                type="text"
                id="description"
                className="hr-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                placeholder="Brief description of the expense"
              />
            </div>

            <div className="rb-field">
              <label htmlFor="attachment">Attachment</label>
              <div className="rb-file">
                <input
                  ref={fileInputRef}
                  id="attachment"
                  type="file"
                  className="rb-file__native"
                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                  aria-invalid={fileError ? "true" : undefined}
                  aria-describedby={
                    fileError ? "attachment-error" : "attachment-hint"
                  }
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="attachment"
                  className="hr-btn hr-btn--secondary rb-file__btn"
                >
                  <Paperclip size={15} aria-hidden="true" />
                  {attachment ? "Change file" : "Choose file"}
                </label>
                <span className="rb-file__name" id="attachment-hint">
                  {attachment
                    ? `${attachment.name} · ${formatFileSize(attachment.size)}`
                    : "JPEG, PNG or PDF · up to 5 MB"}
                </span>
                {attachment && (
                  <button
                    type="button"
                    className="hr-iconbtn"
                    aria-label="Remove attachment"
                    onClick={removeAttachment}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
              {fileError && (
                <p className="rb-error" id="attachment-error" role="alert">
                  {fileError}
                </p>
              )}
            </div>

            <div className="rb-field rb-field--full">
              <label htmlFor="remarks">Remarks *</label>
              <textarea
                id="remarks"
                className="rb-textarea"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                required
                placeholder="Additional details or justification"
                rows="3"
              />
            </div>
          </div>

          <div className="rb-actions">
            <button
              type="submit"
              className="hr-btn hr-btn--primary hr-btn--lg"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </form>
      </section>

      {loadError ? (
        <section className="hr-card">
          <ErrorState
            title="Couldn't load your reimbursements"
            message="Check your connection and try again."
            onRetry={retryRequests}
          />
        </section>
      ) : (
        <GeneralTable
          columns={columns}
          data={rows}
          loading={loading}
          rowKey={(row, index) => row.reimbursementId ?? index}
          title="Reimbursements"
          emptyStateTitle="No reimbursement requests yet"
          emptyStateSubtitle="Requests you submit will be tracked here."
        />
      )}

      <ConfirmationDialog
        open={Boolean(pendingCancelId)}
        title="Cancel Request"
        message="Are you sure you want to cancel this reimbursement request? This action cannot be undone."
        confirmLabel="Yes, Cancel Request"
        cancelLabel="Keep Request"
        busy={cancelling}
        onConfirm={handleCancelConfirmed}
        onCancel={() => setPendingCancelId(null)}
      />
    </div>
  );
}

export default Reimbursements;
